import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { catalogueApi, errorOf, needsMigration, ICONS } from '../../lib/catalogue';
import { portalContractor } from '../../lib/contractorAuth';
import './Catalogue.css';

/**
 * REQUEST SOMETHING (migration 124): the catalogue, and one item's form.
 *
 * /request is the cards - searched by words, filtered by category - of what
 * this person may ask for: the server decides (all staff, a department,
 * named people; a contractor only items marked for contractors too).
 * /request/:itemId is one item: its questions, what it costs as the answers
 * change, and who approves it, in words ("Your manager, Rita Gatt, approves
 * this") - the same sentence the ticket keeps. Sending it raises an ordinary
 * ticket; the page then says which, and links to it and to My requests.
 *
 * A contractor sees it inside the portal's frame (ContractorDashboard's
 * 'request' pane), as Create ticket is; staff on a page of its own.
 *
 * The live quote can be overtaken by a later keystroke, so only the latest
 * answer is shown (a request counter), and the search lives in component
 * state - it is not written to the address, so React Router's lagging copy
 * of the address never comes into it.
 */
export function ItemIcon({ name }) {
  const d = ICONS[name] || ICONS.parcel;
  return (
    <span className="rq-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d={d} />
      </svg>
    </span>
  );
}

function Cards() {
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');

  useEffect(() => {
    let live = true;
    catalogueApi.list()
      .then((d) => { if (live) setData(d); })
      .catch((e) => {
        if (!live) return;
        setProblem(needsMigration(e) ? 'Request something is not set up on this server yet (it needs migration 124).'
          : errorOf(e, 'Could not load the catalogue.'));
      });
    return () => { live = false; };
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.items || []).filter((i) => (!cat || i.category_id === cat)
      && (!needle || `${i.name} ${i.description || ''} ${i.category_name || ''}`.toLowerCase().includes(needle)));
  }, [data, q, cat]);

  return (
    <div className="container rq-page" data-testid="request-catalogue">
      <div className="page-header">
        <h1 className="page-title">Request something</h1>
        <p className="rq-lead">Pick what you need and answer a few questions. It goes to whoever approves it, then to IT - you can follow it under My requests.</p>
      </div>
      {problem && <div className="rq-alert" role="alert">{problem}</div>}
      {!problem && !data && <p className="rq-lead">Loading…</p>}
      {data && (
        <>
          {(data.items || []).length > 0 && (
            <div className="rq-toolbar">
              <input type="search" aria-label="Search the catalogue" placeholder="Search - laptop, software, access…"
                     value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          )}
          {(data.categories || []).length > 1 && (
            <div className="rq-cats" role="group" aria-label="Category">
              <button type="button" className="rq-cat" aria-pressed={!cat} onClick={() => setCat('')}>All</button>
              {data.categories.map((c) => (
                <button key={c.id} type="button" className="rq-cat" aria-pressed={cat === c.id} onClick={() => setCat(cat === c.id ? '' : c.id)}>
                  {c.name}
                </button>
              ))}
            </div>
          )}
          {(data.items || []).length === 0 && (
            <div className="rq-empty" data-testid="catalogue-empty">
              There is nothing to ask for here yet. For anything else, <Link to="/create">create a ticket</Link>.
            </div>
          )}
          {(data.items || []).length > 0 && shown.length === 0 && (
            <div className="rq-empty">Nothing matches that. <button type="button" className="btn btn-plain btn-small" onClick={() => { setQ(''); setCat(''); }}>Show everything</button></div>
          )}
          <div className="rq-grid">
            {shown.map((i) => (
              <Link key={i.id} to={`/request/${i.id}`} className="rq-card" data-item={i.name}>
                <ItemIcon name={i.icon} />
                <span className="rq-card-body">
                  <span className="rq-card-name">{i.name}</span>
                  {i.description && <span className="rq-card-text">{i.description}</span>}
                  <span className="rq-card-meta">{[i.category_name, i.cost_words].filter(Boolean).join(' · ')}</span>
                </span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Field({ f, value, onChange, error, people, licences }) {
  const id = `rq-f-${f.id}`;
  const req = f.required ? <span className="rq-required" aria-hidden="true">*</span> : null;
  const help = f.help ? <span className="rq-help" id={`${id}-help`}>{f.help}</span> : null;
  const described = f.help ? `${id}-help` : undefined;
  let control;
  switch (f.type) {
    case 'textarea':
      control = <textarea id={id} rows={4} value={value ?? ''} aria-describedby={described} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'number':
      control = <input id={id} type="number" inputMode="numeric" min={f.unit_cost ? 0 : undefined} step={f.unit_cost ? 1 : 'any'}
                       value={value ?? ''} aria-describedby={described} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'date':
      control = <input id={id} type="date" value={value ?? ''} aria-describedby={described} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'select':
      control = (
        <select id={id} value={value ?? ''} aria-describedby={described} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose…</option>
          {(f.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.value}{o.cost ? ` (+€${Number(o.cost).toFixed(2)})` : ''}</option>
          ))}
        </select>
      );
      break;
    case 'person':
      control = (
        <select id={id} value={value ?? ''} aria-describedby={described} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose a member of staff…</option>
          {(people || []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.department ? ` - ${p.department}` : ''}</option>)}
        </select>
      );
      break;
    case 'licence':
      control = (
        <select id={id} value={value ?? ''} aria-describedby={described} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose…</option>
          {(licences || []).map((l) => <option key={l.id} value={l.id}>{l.product}{l.vendor ? ` (${l.vendor})` : ''}</option>)}
        </select>
      );
      break;
    case 'checkbox':
      return (
        <div className="rq-field" data-invalid={error ? 'true' : undefined} data-field={f.id}>
          <label className="rq-check" htmlFor={id}>
            <input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
            {f.label}{f.cost ? ` (+€${Number(f.cost).toFixed(2)})` : ''}{req}
          </label>
          {help}
          {error && <span className="rq-field-error">{error}</span>}
        </div>
      );
    default:
      control = <input id={id} type="text" value={value ?? ''} aria-describedby={described} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className="rq-field" data-invalid={error ? 'true' : undefined} data-field={f.id}>
      <label htmlFor={id}>{f.label}{f.type === 'number' && f.unit_cost ? ` (€${Number(f.unit_cost).toFixed(2)} each)` : ''}{req}</label>
      {help}
      {control}
      {error && <span className="rq-field-error">{error}</span>}
    </div>
  );
}

function ItemPage({ itemId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState(null);
  const [answers, setAnswers] = useState({});
  const [approver, setApprover] = useState('');
  const [quote, setQuote] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const asked = useRef(0);

  useEffect(() => {
    let live = true;
    setData(null); setProblem(null); setSent(null); setAnswers({}); setErrors({});
    catalogueApi.item(itemId)
      .then((d) => { if (live) { setData(d); setQuote({ cost_words: d.cost_words, approval: d.approval }); } })
      .catch((e) => { if (live) setProblem(e?.response?.status === 404 ? 'That is not in the catalogue, or not for you.' : errorOf(e, 'Could not load that item.')); });
    return () => { live = false; };
  }, [itemId]);

  // The cost and the approver as the answers change: only the latest answer counts.
  const requote = useCallback((next, who) => {
    const n = ++asked.current;
    catalogueApi.quote(itemId, next, who).then((q) => { if (n === asked.current) setQuote(q); }).catch(() => {});
  }, [itemId]);

  const set = (fid, v) => {
    const next = { ...answers, [fid]: v };
    setAnswers(next);
    if (errors[fid]) setErrors((e) => { const x = { ...e }; delete x[fid]; return x; });
    requote(next, approver);
  };

  const send = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setProblem(null);
    try {
      const out = await catalogueApi.request(itemId, answers, approver || null);
      setSent(out);
      window.scrollTo(0, 0);
    } catch (err) {
      setErrors(err?.response?.data?.fields || {});
      setProblem(errorOf(err, 'That could not be sent.'));
    } finally {
      setBusy(false);
    }
  };

  const portal = portalContractor();
  const ticketLink = (t) => (portal?.id ? `/my-tickets/${t.id}` : `/tickets/${t.id}`);

  if (problem && !data) {
    return (
      <div className="container rq-page">
        <Link to="/request" className="rq-back">← Request something</Link>
        <div className="rq-alert" role="alert">{problem}</div>
      </div>
    );
  }
  if (!data) return <div className="container rq-page"><p className="rq-lead">Loading…</p></div>;
  const item = data.item;
  const approval = quote?.approval || data.approval;

  if (sent) {
    const st = sent.request?.approval_route;
    return (
      <div className="container rq-page" data-testid="request-sent">
        <Link to="/request" className="rq-back">← Request something</Link>
        <div className="rq-done" role="status">
          <p><strong>Sent - {sent.ticket.ticket_number}.</strong> {sent.request?.approval_said}</p>
          <p>
            {st === 'none' || st === 'under' ? 'IT has it now.' : 'You will hear when it is decided.'}{' '}
            <Link to={ticketLink(sent.ticket)}>Open the ticket</Link> · <Link to="/my-requests">My requests</Link>
          </p>
        </div>
        <p><button type="button" className="btn btn-plain" onClick={() => navigate('/request')}>Ask for something else</button></p>
      </div>
    );
  }

  return (
    <div className="container rq-page" data-testid="request-item">
      <Link to="/request" className="rq-back">← Request something</Link>
      <div className="rq-item">
        <form className="rq-form" onSubmit={send} noValidate>
          <div className="rq-item-head">
            <ItemIcon name={item.icon} />
            <h1 className="page-title">{item.name}</h1>
          </div>
          {item.description && <p className="rq-lead">{item.description}</p>}
          {(item.fields || []).map((f) => (
            <Field key={f.id} f={f} value={answers[f.id]} error={errors[f.id]} people={data.people} licences={data.licences}
                   onChange={(v) => set(f.id, v)} />
          ))}
          {approval?.pick && (
            <div className="rq-field" data-invalid={errors.approver_id ? 'true' : undefined} data-field="approver_id">
              <label htmlFor="rq-approver">Who approves it?{approval.required && <span className="rq-required" aria-hidden="true">*</span>}</label>
              <select id="rq-approver" value={approver} onChange={(e) => { setApprover(e.target.value); requote(answers, e.target.value); }}>
                <option value="">{approval.required ? 'Choose an approver…' : 'Any approver'}</option>
                {(data.approvers || []).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}{a.away?.now ? ` (away - ${a.away.delegate_name || 'covered'})` : ''}</option>
                ))}
              </select>
              {errors.approver_id && <span className="rq-field-error">{errors.approver_id}</span>}
            </div>
          )}
          {problem && <div className="rq-alert" role="alert">{problem}</div>}
          <div>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Send the request'}</button>
          </div>
        </form>
        <aside className="rq-side" aria-label="Cost and approval">
          {item.priced && (
            <div>
              <div className="rq-side-label">Cost</div>
              <div className="rq-cost" data-testid="request-cost">{quote?.cost_words || '€0.00'}</div>
            </div>
          )}
          <div>
            <div className="rq-side-label">Approval</div>
            <div className="rq-approval" data-testid="request-approval">{approval?.said}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default function RequestCatalogue() {
  const { itemId } = useParams();
  return itemId ? <ItemPage itemId={itemId} /> : <Cards />;
}
